import { useCallback, useContext } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "@material-ui/styles";
import {
  Theme,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@material-ui/core";
import { Identifier } from "react-admin";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import { useAppData } from "../../../common/hooks/useAppData";
import { AppBarFiltersContainerContext } from "../app-bar-filters/AppBarFiltersContainerContext";
import { isArray } from "lodash";

interface AppbarGroupMultipleFilterProps {
  selectedGroups?: Identifier[];
  onSelectedGroupsChange?: (id: Identifier[]) => void;
}

interface EmptyProps {
  header: boolean;
  theme: Theme;
}

const renderEmptyGroupAppbarFilter = ({ header, theme }: EmptyProps) => {
  return (
    <MenuItem className="appbar-filter-list-item" value="">
      <ListItemIcon
        style={{ color: header ? theme.palette.common.white : "auto" }}
      >
        <LocalShippingIcon />
      </ListItemIcon>
      <ListItemText>
        <Typography variant="body2">All groups</Typography>
      </ListItemText>
    </MenuItem>
  );
};

export const AppbarGroupsMultipleFilter = ({
  selectedGroups,
  onSelectedGroupsChange,
}: AppbarGroupMultipleFilterProps) => {
  const theme: Theme = useTheme();
  const groups = useAppData((x) => x.groups) ?? [];
  const { container: appbarFiltersContainer } = useContext(
    AppBarFiltersContainerContext
  );

  const renderGroupAppbarFilterValue = (id: Identifier, header: boolean) => (
    <MenuItem key={id} className="appbar-filter-list-item" value={id}>
      <ListItemIcon
        style={{ color: header ? theme.palette.common.white : "auto" }}
      >
        <LocalShippingIcon />
      </ListItemIcon>
      <ListItemText>
        <Typography variant="body2">
          {groups.find((x) => x.id === id)?.name}
        </Typography>
      </ListItemText>
    </MenuItem>
  );

  const renderSelectedGroupValues = useCallback(
    (values: Array<string | null | undefined>) => (
      <MenuItem className="appbar-filter-list-item">
        <ListItemIcon style={{ color: theme.palette.common.white }}>
          <LocalShippingIcon />
        </ListItemIcon>
        <ListItemText>
          <Typography variant="body2">{values.join(", ")}</Typography>
        </ListItemText>
      </MenuItem>
    ),
    [theme]
  );

  if (!groups.length) {
    return null;
  }

  return appbarFiltersContainer
    ? createPortal(
        <>
          <Select
            multiple
            autoFocus={false}
            className="appbar-filter companies-filter"
            disableUnderline
            label=""
            value={selectedGroups}
            displayEmpty
            renderValue={(value) => {
              if (!isArray(value) || !value.length) {
                return renderEmptyGroupAppbarFilter({ theme, header: true });
              }

              const names = value.map(
                (v) => groups.find((x) => x.id === v)?.name
              );
              return names
                ? renderSelectedGroupValues(names)
                : renderEmptyGroupAppbarFilter({ theme, header: true });
            }}
            onChange={(e) => {
              if (!onSelectedGroupsChange) {
                return;
              }

              const target = e.target as HTMLSelectElement;
              const value = target.value as string | string[];
              if (!Array.isArray(value)) {
                console.error("The select input value was not array");
                return;
              }

              if (value.some((x) => !x)) {
                onSelectedGroupsChange([]);
                return;
              }

              const values: string[] = [];
              if (!value?.length) {
                onSelectedGroupsChange(values);
                return;
              }

              for (let i = 0, l = value.length; i < l; i += 1) {
                if (value[i]) {
                  values.push(value[i]);
                }
              }
              onSelectedGroupsChange(values);
            }}
          >
            {[
              <MenuItem
                key={0}
                className="appbar-filter-list-item"
                value={undefined}
              >
                <ListItemIcon style={{ color: "auto" }}>
                  <LocalShippingIcon />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body2">All groups</Typography>
                </ListItemText>
              </MenuItem>,
              ...groups.map((x) =>
                renderGroupAppbarFilterValue(x.id ?? "", false)
              ),
            ]}
          </Select>
        </>,
        appbarFiltersContainer
      )
    : null;
};
